import React from 'react';
import './PrivacyPolicy.css';
import { BM_DISCLAIMER, BM_DISCLAIMER_EXTRA } from './legal';

const PrivacyPolicy = () => {
  return (
    <div className='privacy-policy'>
      <h1>Privacy Policy</h1>
      <p><strong>Last updated:</strong> June 7, 2026</p>

      <p><em><b>{BM_DISCLAIMER} {BM_DISCLAIMER_EXTRA}</b></em></p>

      <h2>1. About this policy</h2>
      <p>
        This policy covers Naviguesser ("we," "us," or "our") — our website and our phone and
        watch apps. The same account and the same data work across all of them, so this one
        policy applies everywhere. It explains, in plain language, what we collect, why, and
        what choices you have. It does not cover the physical Burning Man compass device.
      </p>

      <h2>2. What we collect</h2>
      <ul>
        <li><strong>Your email address.</strong> You give this to us when you sign in. If you start signing in with an email link, we store your email so we can finish signing you in.</li>
        <li><strong>Your name.</strong> If you sign in with Google, we receive the display name on your Google account.</li>
        <li><strong>An account ID.</strong> We create an ID for your account and use it to link your data to you.</li>
        <li><strong>Your votes, ratings, and picks.</strong> The events and art you rate, and the picks you save.</li>
        <li><strong>Location (phone and watch apps).</strong> The apps use your location — including in the background — to run the compass and navigate you to events. We store which events you arrived at and how far away you were when you arrived. We do not keep a continuous trail of your location on our servers.</li>
        <li><strong>Group activity.</strong> If you create or join a group, the people in that group can see your picks. You choose who is in your group.</li>
        <li><strong>Feedback.</strong> Anything you send us through our feedback form.</li>
      </ul>
      <p>
        We do not use third-party advertising or analytics trackers in our website or apps.
      </p>

      <h2>3. How we use your information</h2>
      <ul>
        <li>To run the service and keep your account and data in sync across web, phone, and watch.</li>
        <li>To show you events and art to explore, and to navigate you to the ones you pick.</li>
        <li>To improve the service, including by looking at combined, anonymous statistics (for example, which events are popular). These statistics do not identify you.</li>
        <li>To respond to your feedback and requests.</li>
      </ul>

      <h2>4. Location, in detail</h2>
      <p>
        Location is optional. The apps ask your permission before using it, and you can turn
        it off at any time in your device settings. Without it, the compass and turn-by-turn
        navigation will not work, but you can still browse and vote. We use your location only
        to power navigation and to record your event visits — we do not sell or share your
        location, and we do not use it for advertising.
      </p>

      <h2>5. Who we share data with</h2>
      <ul>
        <li><strong>People in your groups.</strong> If you use groups, your picks are shared with the members you choose.</li>
        <li><strong>Service providers.</strong> We store data with Google Firebase (storage and sign-in). Our feedback form is a Google Form, so feedback you submit goes to Google as part of that form.</li>
        <li><strong>Combined, anonymous data.</strong> We may share aggregated statistics that do not identify you.</li>
        <li><strong>Legal reasons.</strong> We may share data if the law requires it.</li>
        <li><strong>We never sell your personal data.</strong></li>
      </ul>

      <h2>6. Where your data is stored</h2>
      <p>
        Your data is stored on Google Firebase, which runs on servers in the United States. If
        you use the service from outside the United States, your data is transferred to and
        stored there.
      </p>

      <h2>7. Your rights and choices</h2>
      <p>
        You can ask us to show you the data we hold about you, correct it, export it, or delete
        it. You can also object to certain uses. To make any of these requests, use the feedback
        form on our service. We aim to respond within two weeks, except during the week of
        Burning Man, when responses may take longer. We do not sell your data, and we honor
        recognized browser "do not sell or share" signals on our website.
      </p>

      <h2>8. How long we keep your data</h2>
      <p>
        We keep your account data — including your votes, picks, and visits — for as long as
        your account is active, or until you ask us to delete it. If you start an email sign-in
        but finish signing in another way, we remove the email we stored for that sign-in once
        it is no longer needed. When you ask us to delete your data, we delete all of the
        categories listed in section 2.
      </p>

      <h2>9. Children</h2>
      <p>
        Naviguesser is intended for adults attending an adults-only event. It is not directed to
        children, and we do not knowingly collect data from anyone under 13. If you believe a
        child has given us their information, contact us through the feedback form and we will
        delete it.
      </p>

      <h2>10. Cookies and local storage</h2>
      <p>
        Our website uses cookies and local storage to keep you signed in and manage your
        session. The embedded Google feedback form may set its own Google cookies. We do not use
        cookies for advertising.
      </p>

      <h2>11. How we protect your data</h2>
      <p>
        We send data over encrypted (HTTPS) connections, store it on Google Firebase using its
        built-in protections, and limit who can access it. No service can promise perfect
        security, but we take reasonable steps to protect your information.
      </p>

      <h2>12. Changes to this policy</h2>
      <p>
        We may update this policy from time to time. We will post any changes on this page and
        update the date at the top. If we make a significant change, we will make the notice
        more prominent.
      </p>

      <h2>13. Contact us</h2>
      <p>
        If you have any questions, concerns, or requests about your privacy, use the feedback
        form on our service to reach us.
      </p>
    </div>
  );
};

export default PrivacyPolicy;
